import { FeeTier, MarketType } from '@velocity-exchange/sdk';
import { cj } from 'src/utils/classNames';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { Open } from '@velocity-exchange/icons';
import useDriftClient from 'src/hooks/useDriftClient';
import React, { useEffect, useMemo, useState } from 'react';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { useShallow } from 'zustand/react/shallow';
import useModal from 'src/components/Modals/hooks/useModal';
import IconButton from 'src/components/primitives/IconButton/IconButton';

interface Props {
	className?: string;
}

const FeeTierValueDisplay = React.memo(function FeeTierValueDisplay({
	className,
}: Props) {
	const selectedMarketId = useDriftStore((s) => s.selectedMarket.marketId);
	const { show } = useModal();
	const userAccountClient = useDriftAccountsStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.client
	);
	const isMobileScreenSize = useIsMobileScreenSize();
	const driftClient = useDriftClient();
	const [tradeSide, postOnly] = useDriftStore(
		useShallow((s) => [s.tradeForm.side, s.tradeForm.postOnly])
	);
	const [feeTier, setFeeTier] = useState<FeeTier>();

	const isZeroFeesPromotion = getIsZeroFeesActiveForMarket({
		marketIndex: selectedMarketId.marketIndex,
		isPerp: selectedMarketId.isPerp,
	});

	useEffect(() => {
		const getFeeTier = async () => {
			try {
				const tier = userAccountClient?.getUserFeeTier(MarketType.PERP);
				setFeeTier(tier);
			} catch (e) {
				try {
					const statsAccount = driftClient?.getUserStats();
					await statsAccount?.subscribe();
					const tier = userAccountClient?.getUserFeeTier(MarketType.PERP);
					setFeeTier(tier);
				} catch (err) {
					console.error('Error getting user fee tier after subscribe', err);
					setFeeTier(undefined);
				}
			}
		};

		getFeeTier();
	}, [userAccountClient, driftClient]);

	const feeTierContent = useMemo(() => {
		if (!userAccountClient || !feeTier) {
			return null;
		}

		const {
			feeNumerator,
			feeDenominator,
			makerRebateNumerator,
			makerRebateDenominator,
		} = feeTier;

		const takerFeePct = (feeNumerator / feeDenominator) * 100;

		const makerFeePct = -(makerRebateNumerator / makerRebateDenominator) * 100;

		const takerFeePctFormatted = parseFloat(takerFeePct.toFixed(4));
		// Maker fee intentionally formatted with a different precision.
		const makerFeePctFormatted = parseFloat(makerFeePct.toFixed(5));

		if (isZeroFeesPromotion) {
			return (
				<>
					<span className="text-text-secondary line-through">
						{postOnly ? makerFeePctFormatted : takerFeePctFormatted}%
					</span>
					<span className="text-star-yellow">
						0% {!isMobileScreenSize && '/ 0%'}
					</span>
				</>
			);
		}

		if (!isMobileScreenSize) {
			return `${takerFeePctFormatted}% / ${makerFeePctFormatted}%`;
		}

		if (!postOnly) {
			return `${takerFeePctFormatted}%`;
		}

		return `${makerFeePctFormatted}%`;
	}, [
		selectedMarketId.marketIndex,
		driftClient,
		userAccountClient,
		feeTier,
		tradeSide,
		isZeroFeesPromotion,
		isMobileScreenSize,
		postOnly,
	]);

	const handleClick = () => {
		show({ type: 'fee_tier', props: {} });
	};

	return (
		<div
			className={cj(
				'flex items-center justify-between typo-body-xs',
				className
			)}
		>
			<span className="text-text-secondary">Fees</span>
			<span className="flex items-center gap-x-xs text-text-default">
				{feeTierContent ?? '-'}
				{feeTierContent && (
					<IconButton
						size="sm"
						variant="tertiary"
						className="-m-sm p-sm"
						onClick={handleClick}
						icon={Open}
					/>
				)}
			</span>
		</div>
	);
});

export default FeeTierValueDisplay;
